// src/components/TicketDetailViewSupport.tsx
import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchTicketById,
  closeTicket,
  addNoteToTicket,
  updateTicketStatus,
  getPreviousNotes,
  fetchEquipment,
  addEquipmentToTicket,
} from "../api";
import { DistributedEquipment, Equipment, PreviousNote, Ticket } from "../types";
import cardStyles from "../styles/Card.module.css";
import buttonStyles from "../styles/Button.module.css";
import adminStyles from "../styles/AdminView.module.css";
import { NoteComponent } from "./NoteComponent";
import { AlertModalContext } from "./AlertModal";
import { GlobalContext } from "../App";
import { useAuth } from "react-oidc-context";
import PreviousTickets from "./PreviousTickets";

export default function TicketDetailViewSupport() {
  const auth = useAuth();
  const { site, id } = useParams<{ site: string, id: string }>();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [availableEquipment, setAvailableEquipment] = useState<DistributedEquipment[]>([]);
  const [newNoteText, setNewNoteText] = useState<string>();
  const [modalOpen, setModalOpen] = useState(false);
  const [closingNote, setClosingNote] = useState("");
  const [previousNotes, setPreviousNotes] = useState<PreviousNote[]>([]);
  const [noteFilter, setNoteFilter] = useState("");
  const navigate = useNavigate();

  const {globalSite, setSite} = useContext(GlobalContext);
  useEffect(() => {
      if (globalSite === undefined || globalSite === "") {
        if (typeof auth.user?.profile.site === "string") {
          setSite(auth.user?.profile.site)
        } else if (typeof site === "string") {
          setSite(site)
        }
      }
    }, [globalSite, site, auth.user?.profile.site, setSite])


  useEffect(() => {
    getPreviousNotes().then(setPreviousNotes)
  }, [setPreviousNotes])

  useEffect(() => {
    if (globalSite) {
      fetchEquipment(globalSite).then((equipment: Equipment[]) => {
        const dist = equipment.map((e:Equipment) => {
          let dist:DistributedEquipment = {
            id: e.id,
            description: e.description,
            distributed: false
          };
          return dist
        })
        setAvailableEquipment([{
          id: -1,
          description: "None",
          distributed: false
        }, ...dist])
      });
    }
  }, [globalSite])

  // alert modal state
  const { setShowAlertModal, setAlertMessage } = useContext(AlertModalContext);

  // modal open/close
  const openAlertModal = (text:string) => {
    setAlertMessage(text);
    setShowAlertModal(true);
  }

  useEffect(() => {
    fetchTicketById(Number(id)).then(setTicket);
  }, [id]);

  if (!ticket) return <p>Loading…</p>;


  const openModal = () => setModalOpen(true);
  const closeModal = () => {
    setModalOpen(false);
    setClosingNote("");
    resetEquipment();
  }
  const onClose = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateClosingEquipment()) {
      openAlertModal("Please select equipment distributed during support process. If no equipment was distributed select 'None'")
      return;
    }
    let equipment:string[] = availableEquipment
      .filter(e => e.distributed)
      .map((e) => {
        return e.description
      }
    )
    console.log(equipment)

    await addNoteToTicket(ticket.id, new Date().toISOString(), closingNote).then((ticket) => {
      addEquipmentToTicket(ticket.id, equipment);
      updateTicketStatus(ticket.id, "Closed").then(setTicket)
    });

    setClosingNote("");
    closeModal();
  };
  
  function validateClosingEquipment() {
    if (availableEquipment.findIndex(e => e.distributed) === -1) {
      return false;
    }
    if (availableEquipment.findIndex(e => e.description === "None") && availableEquipment.length !== 1) {
      return false
    }
    return true;
  }

  function resetEquipment() {
    const equip = availableEquipment.map((e:Equipment) => {
      return {
        id: e.id,
        description: e.description,
        distributed: false
      }
    })
    setAvailableEquipment(equip)
  }

  const Begin = async () => {
    const updated = await updateTicketStatus(ticket.id, "InProgress");
    setTicket(updated);
  };

  const Escalated = async () => {
    if (ticket.notes.length === 0) {
      openAlertModal("Cannot Escalate To Unify Without Any Notes");
      return;
    }
    const updated = await updateTicketStatus(ticket.id, "EscalatedToUnify");
    setTicket(updated);
  };

  function displayStatus() {
    switch (ticket?.status) {
      case "Requested":
        return <span className={buttonStyles.statusOpen}>Open</span>;
      case "InProgress":
        return (
          <span className={buttonStyles.statusInProgress}>In Progress</span>
        );
      case "EscalatedToUnify":
        return <span className={buttonStyles.statusEscalated}>Escalated</span>;
      case "Closed":
        return <span className={buttonStyles.statusClosed}>Closed</span>;
      default:
        return null;
    }
  }

  function createNewNote() {
    if (newNoteText === undefined
      || newNoteText.trim() === ""
      || ticket?.id === undefined) {
      return;
    }
    addNoteToTicket(ticket?.id, new Date().toISOString(), newNoteText).then((result) => {
      setTicket(result);
      if (ticket.status === "Requested") {
        Begin();
      }
    });
    setNewNoteText("");
    setNoteFilter("");
    getPreviousNotes().then(setPreviousNotes)
  }

  function displayNotes() {
    if (ticket === null || ticket.notes.length === 0) {
      return <div>No Notes</div>;
    }
    return ticket.notes.map((note, i) => <NoteComponent key={i} {...note} />);
  }

  function displaySuggestions(): React.ReactNode {
    return (
      <div
        className={cardStyles.card}
      >Suggested notes [Click to copy]:
        {previousNotes
          .filter(n => n.text.toLowerCase().includes(noteFilter.toLowerCase())).slice(0, 5)
          .map(n => 
            <div
              style={{
                border: "none",
                borderRadius: "8px",
                padding: "8px 16px",
                margin: "1px",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
              }}
              onMouseEnter={(e) => {(e.target as HTMLDivElement).style.backgroundColor = "#aaaaaaaa"}}
              onMouseLeave={(e) => {(e.target as HTMLDivElement).style.backgroundColor = "white"}}
              key={n.text}
              onClick={() => {setNewNoteText(n.text)}}
            >
              {n.text}
            </div>)
          }
        </div>
    )
  }

  function displayRating() {
    let ratingClass = buttonStyles.statusEscalated
    let ratingText = "Unrated"

    switch (ticket?.rating) {
      case "Positive":
        ratingClass = buttonStyles.statusOpen
        ratingText = "Positive"
        break;
      case "Negative":
        ratingClass = buttonStyles.statusClosed
        ratingText = "Negative"
        break;
      default:
        break;
    }

    return (
      <div className={cardStyles.card}>
        <h4>Rating</h4>
        <div>User rated experience as:
          <span
            className={ratingClass}
            style={{
              border: "none",
              padding: "8px 16px",
              margin: 8
            }}
            >
              {ratingText}
            </span>
        </div>
      </div>
    )
  }

  function handleNoteUpdate(event: React.ChangeEvent<HTMLInputElement>): void {
    setNewNoteText(event.target.value)
    setNoteFilter(event.target.value)
  }
  
  function handleChange(e:React.ChangeEvent<HTMLInputElement>) {
    const target = e.target;
    const value = target.type === "checkbox" ? target.checked : false;
    const name = target.name;

    const updatedEquipment:DistributedEquipment[] = availableEquipment.map(equipment => {
      if (equipment.description !== name) {
        return equipment;
      } else {
        return {
          ...equipment,
          distributed: value
        }
      }
    });
    setAvailableEquipment(updatedEquipment)
  }

  return (
    <div>
      <div className={cardStyles.card} style={{width : "100%"}}>
        <button
          onClick={() => navigate(-1)}
          style={{
            marginBottom: 16,
            borderRadius: 8,
            border: "none",
            backgroundColor: "#f0f0f0",
            padding: "8px 16px",
            cursor: "pointer",
          }}
        >
          ← Back
        </button>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2>Ticket #{ticket.id}</h2>
          {displayStatus()}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 24,
          }}
        >
          <div>
            <h4>Fault(s)</h4>
            {ticket.faults.map((f, i) => (
              <p key={i}>{f}</p>
            ))}
          </div>
          <div>
            <h4>User</h4>
            <p>{ticket.userName}</p>
            {ticket.submittedByUser !== ticket.userName && (
              <p style={{ fontStyle: "italic", color: "#888" }}>
                Submitted by: {ticket.submittedByUser}
              </p>
            )}
          </div>
          <div>
            <h4>Cell number</h4>
            <p>{ticket.location}</p>
          </div>
        </div>
        <div>
          <h4>Notes</h4>
          {displayNotes()}
        </div>
        {ticket.status !== "Closed" ? (
          <div style={{ marginTop: 16 }}>
            <h4>New Note</h4>
            <input
              id="noteInput"
              type="text"
              value={newNoteText}
              onChange={handleNoteUpdate}
              placeholder="New Note"
              style={{
                width: "100%",
                padding: "8px 8px",
                borderRadius: 4,
                border: "1px solid #ccc",
              }}
            />
            <div>
              {displaySuggestions()}
            </div>
            <button
              onClick={createNewNote}
              style={{
                marginTop: 16,
                marginBottom: 16,
                borderRadius: 8,
                border: "none",
                backgroundColor: "#007BFF",
                color: "white",
                padding: "8px 16px",
                cursor: "pointer",
              }}
            >
              Create New Note
            </button>
          </div>
        ) : displayRating()}
        {/* Display Equipment */}
        {ticket.status === "Closed" ? (
          <div>
            <h4>Equipment Distributed:</h4>
            {ticket.equipment.map((e) => (
              <div key={e}>{e}</div>
            ))}
          </div>
        ) : null}
        <div
          style={{
            display: "flex",
            gap: 16,
            justifyContent: "flex-end",
            marginTop: 32,
          }}
        >
          {ticket.status !== "Closed" && (
            <button
              className={`${buttonStyles.button} ${buttonStyles.close}`}
              onClick={openModal}
            >
              Close
            </button>
          )}
          {ticket.status === "Requested" && (
            <button className={`${buttonStyles.button}`} onClick={Begin}>
              Begin
            </button>
          )}
          {ticket.status === "InProgress" && (
            <button className={`${buttonStyles.button}`} onClick={Escalated}>
              Escalate To Unify
            </button>
          )}
        </div>
        {/* Close Ticket Modal */}
        {modalOpen && (
          <div className={adminStyles.modalOverlay} onClick={closeModal}>
            <div
              className={`${cardStyles.card} ${adminStyles.modalContent}`}
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Close Ticket</h3>
              <div>Please add final note and select any equipment distributed during support process, before confirming ticket closure.</div>
              <form className={adminStyles.modalForm} onSubmit={onClose}>
                <div className={adminStyles.modalField}>
                  <label>Closing Note</label>
                  <input
                    id="closingNote"
                    value={closingNote}
                    onChange={(e) => setClosingNote(e.target.value)}
                    required
                  />
                </div>
                <div className={adminStyles.modalField}>
                  <label>Equipment Distributed</label>
                  <div
                    style={{
                      display: "flex"
                    }}
                  >
                    {availableEquipment.map((e) => (
                        <label
                          key={e.id}
                          style={{
                            margin: "5px",
                            padding: "5px",
                            display: "flex",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                          }}
                        >{e.description}
                          <input
                            type="checkbox"
                            name={e.description}
                            checked={e.distributed}
                            onChange={handleChange}
                          />
                        </label>
                    ))}
                  </div>
                </div>
                <div className={adminStyles.modalActions}>
                  <button
                    type="button"
                    className={`${buttonStyles.button} ${buttonStyles.secondary}`}
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`${buttonStyles.button} ${buttonStyles.primary}`}
                  >
                    Confirm
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <PreviousTickets {...ticket}/>
    </div>
  );
}
